import { ArrowsChevronDown } from "@fanduel/formation-react-icons";
import { fanduel as fd } from "@fanduel/formation-tokens";
import { Loader } from "@fanduel/og-fe-library";
import { useMemo } from "react";
import ErrorComponent from "@/components/empty/ErrorComponent";
import { PLAYER_NEWS_ORIGIN_ARTICLE_CARD } from "@/constants/amplitude";
import { ShortForm } from "@/graphql/gql/graphql";
import PlayerNewsCard from "../PlayerNewsCard";
import styles from "./playerNewsList.module.scss";

interface PlayerNewsListProps {
  news: ShortForm[];
  showMore?: () => void;
  loading?: boolean;
}

const noNewsContent = (
  <div className={styles.errorVanContainer}>
    <ErrorComponent
      headline="No player news available"
      subtext="Please check back later."
      showCta={false}
    />
  </div>
);

const PlayerNewsList: React.FC<PlayerNewsListProps> = ({
  news,
  showMore,
  loading,
}: PlayerNewsListProps) => {
  const loadingContent = (className: string) => (
    <div className={className}>
      <Loader size="medium" />
    </div>
  );

  // Check if there is news (non-null and has a length greater than 0)
  const hasNews = news && news.length > 0;

  const shouldRenderNewsCards = useMemo(
    () =>
      hasNews
        ? news.map((playerNews) => (
            <PlayerNewsCard
              origin={PLAYER_NEWS_ORIGIN_ARTICLE_CARD}
              key={playerNews.id}
              news={playerNews}
            />
          ))
        : null,
    [hasNews, news]
  );

  const loadingDiv = loading
    ? loadingContent(styles.loadingContainerLoadMore)
    : null;

  // Determine if no news content should be rendered based on loading and hasNews.
  if (!loading && !hasNews) {
    return noNewsContent;
  }

  return (
    <div className={styles.listContainer}>
      {shouldRenderNewsCards}

      {loadingDiv}

      {showMore && (
        <button
          type="button"
          data-testid="show-more-btn"
          onClick={showMore}
          className={styles.button}
        >
          Show More Player News
          <ArrowsChevronDown color={fd.colors.coreBlueD1} />
        </button>
      )}
    </div>
  );
};

export default PlayerNewsList;
